import React, { useState, useRef, useEffect } from 'react';
import axios from 'axios';
import { X, Send, Bot, User, Table, TrendingUp, Package, Ship } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';
import VoiceAssistant from './VoiceAssistant';
import { baseURL } from '@/config/APIConfig';


const API = baseURL.dashboardApi;
// const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
// const API = BACKEND_URL ? `${BACKEND_URL.replace(/\/$/, '')}/api` : '/api';


// Component to render structured data as a table
const DataTable = ({ data, title, type }) => {
    if (!data || (Array.isArray(data) && data.length === 0)) return null;

    // Handle array of objects
    if (Array.isArray(data)) {
        const headers = Object.keys(data[0] || {});

        return (
            <div className="mt-3 overflow-hidden rounded-lg border border-slate-200 dark:border-slate-700">
                <div className="bg-gradient-to-r from-blue-500 to-cyan-500 px-3 py-2">
                    <div className="flex items-center space-x-2">
                        {type === 'booking' && <Package className="w-4 h-4 text-white" />}
                        {type === 'ratequote' && <TrendingUp className="w-4 h-4 text-white" />}
                        {type === 'container' && <Ship className="w-4 h-4 text-white" />}
                        <span className="text-sm font-semibold text-white">{title}</span>
                    </div>
                </div>
                <div className="overflow-x-auto">
                    <table className="w-full text-xs">
                        <thead className="bg-slate-50 dark:bg-slate-800">
                            <tr>
                                {headers.map((header) => (
                                    <th key={header} className="px-3 py-2 text-left font-semibold text-slate-700 dark:text-slate-300 capitalize whitespace-nowrap">
                                        {header.replace(/_/g, ' ')}
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100 dark:divide-slate-700">
                            {data.map((row, idx) => (
                                <tr key={idx} className="hover:bg-slate-50 dark:hover:bg-slate-800/50">
                                    {headers.map((header) => (
                                        <td key={header} className="px-3 py-2 text-slate-600 dark:text-slate-400 whitespace-nowrap">
                                            {header === 'rank' ? (
                                                <span className="inline-flex items-center justify-center w-6 h-6 bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 rounded-full font-bold text-xs">
                                                    {row[header]}
                                                </span>
                                            ) : (
                                                row[header]
                                            )}
                                        </td>
                                    ))}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
        );
    }

    // Handle object with nested arrays (like port distribution)
    if (typeof data === 'object' && !Array.isArray(data)) {
        return (
            <div className="mt-3 space-y-3">
                {Object.entries(data).map(([key, value]) => {
                    if (Array.isArray(value) && value.length > 0) {
                        return (
                            <div key={key} className="overflow-hidden rounded-lg border border-slate-200 dark:border-slate-700">
                                <div className="bg-slate-100 dark:bg-slate-800 px-3 py-2">
                                    <span className="text-xs font-semibold text-slate-700 dark:text-slate-300 capitalize">
                                        {key.replace(/_/g, ' ')}
                                    </span>
                                </div>
                                <div className="p-2 space-y-1">
                                    {value.map((item, idx) => (
                                        <div key={idx} className="flex justify-between text-xs px-2 py-1 bg-slate-50 dark:bg-slate-900 rounded">
                                            <span className="text-slate-600 dark:text-slate-400">{item.port || item.carrier}</span>
                                            <span className="font-semibold text-slate-900 dark:text-white">{item.containers || item.count}</span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        );
                    }
                    return null;
                })}
            </div>
        );
    }

    return null;
};

const AIChatBot = ({ isOpen, onClose, onNavigate }) => {
    const [messages, setMessages] = useState([
        {
            role: 'assistant',
            content: 'Hello! I\'m your AI logistics assistant. I can help you analyze your booking data, rate quotes, and container tracking. Try asking:\n\n• "What are the top carriers?"\n• "Show rate quote conversion rate"\n• "Top routes by booking count"',
            // content: 'Hello! I\'m your AI logistics assistant. I can help you analyze your booking data, rate quotes, and container tracking. Try asking:\n\n• "Show my top 5 customers based on booking"\n• "What are the top carriers?"\n• "Show rate quote conversion rate"\n• "Top routes by booking count"',
            structuredData: null
        }
    ]);
    const [input, setInput] = useState('');
    const [loading, setLoading] = useState(false);
    const [sessionId] = useState(`session-${Date.now()}`);
    const messagesEndRef = useRef(null);

    const scrollToBottom = () => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    };

    useEffect(() => {
        scrollToBottom();
    }, [messages]);

    const handleSend = async () => {
        if (!input.trim()) return;

        const userMessage = { role: 'user', content: input, structuredData: null };
        setMessages((prev) => [...prev, userMessage]);
        const queryText = input;
        setInput('');
        setLoading(true);

        try {
            // Use the chatbot/query endpoint for data queries
            const response = await axios.post(`${API}/chatbot/query`, {
                query: queryText,
                session_id: sessionId
            });

            const assistantMessage = {
                role: 'assistant',
                content: response.data.response,
                structuredData: response.data.structured_data
            };
            setMessages((prev) => [...prev, assistantMessage]);
        } catch (error) {
            console.error('Error sending message:', error);

            // Fallback to ai/chat endpoint
            try {
                const fallbackResponse = await axios.post(`${API}/ai/chat`, {
                    message: queryText,
                    session_id: sessionId
                });

                const assistantMessage = {
                    role: 'assistant',
                    content: fallbackResponse.data.response,
                    structuredData: null
                };
                setMessages((prev) => [...prev, assistantMessage]);
            } catch (fallbackError) {
                toast.error('Failed to get response from AI');
                const errorMessage = {
                    role: 'assistant',
                    content: 'Sorry, I encountered an error. Please try again.',
                    structuredData: null
                };
                setMessages((prev) => [...prev, errorMessage]);
            }
        } finally {
            setLoading(false);
        }
    };

    const handleKeyPress = (e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleSend();
        }
    };

    const handleVoiceTranscription = (text) => {
        setInput(text);
        // Automatically send the transcribed text
        setTimeout(() => {
            handleSend();
        }, 100);
    };

    return (
        <AnimatePresence>
            {isOpen && (
                <motion.div
                    initial={{ x: 400, opacity: 0 }}
                    animate={{ x: 0, opacity: 1 }}
                    exit={{ x: 400, opacity: 0 }}
                    transition={{ duration: 0.3 }}
                    className="fixed right-0 top-0 h-screen w-full md:w-[650px] lg:w-[700px] bg-white dark:bg-slate-900 shadow-2xl z-50 flex flex-col border-l border-slate-200 dark:border-slate-800"
                    data-testid="ai-chatbot"
                >
                    {/* Header */}
                    <div className="bg-gradient-to-r from-[#0072FF] to-[#00C6FB] p-6 text-white">
                        <div className="flex items-center justify-between">
                            <div className="flex items-center space-x-3">
                                <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center">
                                    <Bot className="w-6 h-6" />
                                </div>
                                <div>
                                    <h3 className="text-lg font-bold">AI Assistant</h3>
                                    <p className="text-xs text-white/80">Powered by Shipez AI • Data Analysis</p>
                                </div>
                            </div>
                            <button
                                onClick={onClose}
                                data-testid="close-chat-btn"
                                className="p-2 hover:bg-white/20 rounded-lg transition-colors"
                                aria-label="Close chat"
                            >
                                <X className="w-6 h-6" />
                            </button>
                        </div>
                    </div>

                    {/* Messages */}
                    <div className="flex-1 overflow-y-auto overflow-x-hidden p-4 space-y-4" data-testid="chat-messages">
                        {messages.map((message, index) => (
                            <div
                                key={index}
                                className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'
                                    }`}
                            >
                                <div
                                    className={`flex space-x-3 max-w-[90%] ${message.role === 'user' ? 'flex-row-reverse space-x-reverse' : ''
                                        }`}
                                >
                                    <div
                                        className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${message.role === 'user'
                                            ? 'bg-[#0072FF]'
                                            : 'bg-gradient-to-br from-[#0072FF] to-[#00C6FB]'
                                            }`}
                                    >
                                        {message.role === 'user' ? (
                                            <User className="w-5 h-5 text-white" />
                                        ) : (
                                            <Bot className="w-5 h-5 text-white" />
                                        )}
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        {/* Only show text message if there's NO structured data */}
                                        {!message.structuredData && (
                                            <div
                                                className={`px-4 py-3 rounded-2xl overflow-hidden ${message.role === 'user'
                                                    ? 'bg-[#0072FF] text-white'
                                                    : 'bg-slate-100 dark:bg-slate-800 text-slate-900 dark:text-white'
                                                    }`}
                                            >
                                                <div className="text-sm leading-relaxed whitespace-pre-wrap break-words overflow-x-auto">
                                                    <p className="break-words">{message.content}</p>
                                                </div>
                                            </div>
                                        )}

                                        {/* For user messages, always show the content */}
                                        {message.role === 'user' && message.structuredData && (
                                            <div className="px-4 py-3 rounded-2xl overflow-hidden bg-[#0072FF] text-white">
                                                <div className="text-sm leading-relaxed whitespace-pre-wrap break-words">
                                                    <p className="break-words">{message.content}</p>
                                                </div>
                                            </div>
                                        )}

                                        {/* Render structured data table ONLY - no text response */}
                                        {message.structuredData && (
                                            <DataTable
                                                data={message.structuredData.data}
                                                title={message.structuredData.title}
                                                type={message.structuredData.type}
                                            />
                                        )}
                                    </div>
                                </div>
                            </div>
                        ))}
                        {loading && (
                            <div className="flex justify-start">
                                <div className="flex space-x-3 max-w-[85%]">
                                    <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-[#0072FF] to-[#00C6FB] flex items-center justify-center">
                                        <Bot className="w-5 h-5 text-white" />
                                    </div>
                                    <div className="px-4 py-3 rounded-2xl bg-slate-100 dark:bg-slate-800">
                                        <div className="flex space-x-2">
                                            <div className="w-2 h-2 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
                                            <div className="w-2 h-2 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
                                            <div className="w-2 h-2 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}
                        <div ref={messagesEndRef} />
                    </div>

                    {/* Input */}
                    <div className="p-4 border-t border-slate-200 dark:border-slate-800">
                        <div className="flex items-center space-x-3">
                            <textarea
                                value={input}
                                onChange={(e) => setInput(e.target.value)}
                                onKeyPress={handleKeyPress}
                                placeholder="Ask about bookings, customers, rates..."
                                data-testid="chat-input"
                                className="flex-1 px-4 py-3 bg-slate-100 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl text-sm text-slate-900 dark:text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-[#0072FF] resize-none"
                                rows="1"
                                disabled={loading}
                            />
                            <VoiceAssistant
                                inline
                                onTranscriptionComplete={handleVoiceTranscription}
                                onNavigate={onNavigate}
                            />
                            <button
                                onClick={handleSend}
                                disabled={loading || !input.trim()}
                                data-testid="send-message-btn"
                                className="p-3 bg-gradient-to-r from-[#0072FF] to-[#00C6FB] hover:shadow-lg hover:scale-105 disabled:bg-slate-300 dark:disabled:bg-slate-700 text-white rounded-xl transition-all disabled:cursor-not-allowed"
                                aria-label="Send message"
                            >
                                <Send className="w-5 h-5" />
                            </button>
                        </div>
                    </div>
                </motion.div>
            )}
        </AnimatePresence>
    );
};

export default AIChatBot;
